import * as React from "react";
import { Button } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { sequence } from "@repo/shared/utils/misc";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ItemCardSkeleton } from "./ItemCard.tsx";
import "./items.css";

interface Props<T> {
  items: T[] | undefined;
  isLoading?: boolean;
  onAddItem?: () => void;
  renderItem: (item: T) => React.ReactNode;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  /** What to say when there's nothing in the list */
  empty: { title: string; detail: string };
}

/**
 * A list of item cards (one column on a phone, a grid wider than that; items.css), loading more
 * as you near the bottom. The page around it does the scrolling (layout/MainPageLayout.tsx).
 */
export function ItemsScrollArea<T>({
  items,
  onAddItem,
  renderItem,
  canLoadMore,
  isLoading,
  onLoadMore,
  empty,
}: Props<T>) {
  const [parent] = useAutoAnimate();

  const { ref, inView } = useInView({
    delay: 500,
    rootMargin: "400px 0px",
  });

  useEffect(() => {
    if (!inView) return;
    if (!canLoadMore) return;
    onLoadMore?.();
  }, [inView, canLoadMore]);

  if (isLoading || !items)
    return (
      <div className="item-list">
        {sequence(9).map((index) => (
          <ItemCardSkeleton key={index} video={index % 4 == 0} />
        ))}
      </div>
    );

  if (items.length == 0)
    return (
      <div className="list-empty">
        <img src="/logo.webp" alt="" />
        <h2>{empty.title}</h2>
        <p>{empty.detail}</p>
        {onAddItem ? (
          <Button mt="3" size="3" onClick={onAddItem}>
            <PlusIcon /> Stash a link
          </Button>
        ) : null}
      </div>
    );

  return (
    <>
      <div className="item-list" ref={parent}>
        {items.map(renderItem)}
      </div>
      <div className="list-more" ref={ref}>
        {canLoadMore ? "Fetching more…" : "That's everything"}
      </div>
    </>
  );
}
